/**
 * Evidence as BACKING STATUS — what an investor without data-room access sees
 * in place of quotes (owner rule, 2026-10-19; shape produced by
 * public.score_event_investor_view).
 *
 * Quotes, their sources and claim text are proof material: the data room is
 * the pass to them. Without it a sub-score carries `evidenceSummary.citations`
 * and a `claimsSummary`; these helpers turn that into the lines the score screen
 * renders, and decide when "no evidence" is actually true.
 */

export interface ClaimsSummary {
  verified: number;
  contradicted: number;
  unverifiable: number;
  unchecked: number;
}

const ORDER: ReadonlyArray<keyof ClaimsSummary> = ['verified', 'contradicted', 'unverifiable', 'unchecked'];

export function formatCitations(n: number): string {
  return `Evidence: ${n} ${n === 1 ? 'citation' : 'citations'}`;
}

/** "Claims: 2 verified · 1 unverifiable", or null when no claim was extracted. */
export function formatClaimsSummary(summary: ClaimsSummary | undefined): string | null {
  if (!summary) return null;
  const parts = ORDER.filter((k) => (summary[k] ?? 0) > 0).map((k) => `${summary[k]} ${k}`);
  return parts.length > 0 ? `Claims: ${parts.join(' · ')}` : null;
}

export type SubScoreEvidenceLine =
  | { kind: 'quotes' }
  | { kind: 'citations'; text: string }
  | { kind: 'gap' };

/**
 * What to render for a sub-score's evidence. Quotes when present; otherwise the
 * citation count when the server withheld the quotes; "gap" ONLY when there is
 * really nothing (zero citations, or an empty quote list in the full view).
 */
export function subScoreEvidenceLine(sub: {
  evidence?: ReadonlyArray<unknown>;
  evidenceSummary?: { citations: number };
}): SubScoreEvidenceLine {
  if ((sub.evidence?.length ?? 0) > 0) return { kind: 'quotes' };
  const citations = sub.evidenceSummary?.citations ?? 0;
  if (citations > 0) return { kind: 'citations', text: formatCitations(citations) };
  return { kind: 'gap' };
}
